import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatARS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar as CalIcon, MapPin, Phone, Check, XCircle, Clock, AlertTriangle, ArrowLeft, Navigation, CalendarPlus } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function Ticket() {
  const { code } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/turno/${code}`);
      setTicket(data);
    } catch (e) {
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [code]);

  const cancelNow = async () => {
    if (!window.confirm("¿Cancelar este turno? Esta acción no se puede deshacer.")) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/turno/${code}/cancel`);
      setTicket(data);
      toast.success("Turno cancelado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cancelar");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-emerald-500 font-heading text-2xl">CARGANDO...</div>;

  if (!ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0A0A] text-white">
        <div className="max-w-md w-full bg-[#141414] border border-[#262626] rounded-lg p-10 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-amber-400" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl tracking-wider mb-2">TURNO NO ENCONTRADO</h2>
          <p className="text-zinc-400 text-sm">El código no existe o ya no es válido.</p>
          <Link to="/" className="inline-block mt-6 text-emerald-400 text-xs uppercase tracking-widest">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  const brand = ticket.org_brand_color || "#10B981";
  const cancelDeadline = ticket.cancel_deadline ? new Date(ticket.cancel_deadline) : null;

  const statusLabel = { pending: "Pendiente de confirmación", confirmed: "Confirmado", cancelled: "Cancelado" }[ticket.status];
  const statusColor = ticket.status === "confirmed" ? "#10B981" : ticket.status === "pending" ? "#F59E0B" : "#EF4444";

  const mapsEmbed = ticket.org_address
    ? `https://www.google.com/maps?q=${encodeURIComponent(ticket.org_address)}&output=embed`
    : null;
  const mapsDirections = ticket.org_address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ticket.org_address)}`
    : null;

  const downloadIcs = () => {
    // Build a simple .ics file for this booking
    const pad = (n) => String(n).padStart(2, "0");
    const [h, m] = ticket.start_time.split(":").map(Number);
    const start = new Date(`${ticket.date}T${ticket.start_time}:00`);
    const end = new Date(start.getTime() + (ticket.duration_minutes || 60) * 60000);
    const fmt = (d) =>
      `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
    const escape = (s) => (s || "").replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Messirve//ES",
      "BEGIN:VEVENT",
      `UID:${ticket.booking_id}@messirve`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:${escape(`${ticket.court_name} · ${ticket.org_name}`)}`,
      `DESCRIPTION:${escape(`Turno en ${ticket.org_name}. Link: ${window.location.href}`)}`,
      ticket.org_address ? `LOCATION:${escape(ticket.org_address)}` : "",
      "END:VEVENT",
      "END:VCALENDAR",
    ].filter(Boolean).join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `turno-${ticket.public_code || ticket.booking_id}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    // suppress unused warning
    void h; void m;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8">
        <Link to={`/book/${ticket.org_slug}`} className="text-zinc-500 text-xs uppercase tracking-widest hover:text-white flex items-center gap-2 mb-6">
          <ArrowLeft className="h-3 w-3" /> {ticket.org_name}
        </Link>

        <div className="bg-[#141414] border border-[#262626] rounded-lg overflow-hidden">
          <div className="p-8 border-b border-[#262626]" style={{ background: `linear-gradient(135deg, ${brand}20, transparent)` }}>
            <div className="flex items-center gap-4 mb-4">
              {ticket.org_logo_url ? (
                <img src={ticket.org_logo_url} alt="" className="h-12 w-12 rounded-lg object-cover" />
              ) : (
                <div className="h-12 w-12 rounded-lg flex items-center justify-center font-heading text-2xl" style={{ background: `${brand}30`, color: brand, border: `1px solid ${brand}` }}>
                  {ticket.org_name[0]}
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Tu turno en</div>
                <div className="font-heading text-2xl tracking-wider" data-testid="ticket-org">{ticket.org_name}</div>
              </div>
            </div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs uppercase tracking-[0.2em] font-bold"
              style={{ background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}40` }}
              data-testid="ticket-status"
            >
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: statusColor }} />
              {statusLabel}
            </div>
          </div>

          <div className="p-8 space-y-5">
            <Detail icon={CalIcon} label="Fecha" value={format(new Date(ticket.date + "T00:00"), "EEEE d 'de' MMMM yyyy", { locale: es })} />
            <Detail icon={Clock} label="Hora" value={`${ticket.start_time} · ${ticket.duration_minutes} min`} />
            <Detail label="Cancha" value={ticket.court_name} />
            <Detail label="A nombre de" value={ticket.customer_name} />
            <Detail label="Precio" value={formatARS(ticket.price_ars)} big brand={brand} />
            {ticket.org_address && <Detail icon={MapPin} label="Dirección" value={ticket.org_address} />}
            {ticket.org_phone && <Detail icon={Phone} label="Contacto del club" value={ticket.org_phone} />}
            {ticket.notes && <Detail label="Nota" value={ticket.notes} />}

            {/* Quick actions */}
            {ticket.status !== "cancelled" && (
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button
                  type="button"
                  onClick={downloadIcs}
                  variant="outline"
                  className="border-zinc-700 bg-transparent text-white hover:bg-[#1E1E1E] uppercase tracking-widest text-xs"
                  data-testid="ticket-ics-btn"
                >
                  <CalendarPlus className="h-3 w-3 mr-2" /> Agendar
                </Button>
                {mapsDirections ? (
                  <a
                    href={mapsDirections}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-md border border-zinc-700 bg-transparent text-white hover:bg-[#1E1E1E] uppercase tracking-widest text-xs font-medium h-10"
                    data-testid="ticket-directions-btn"
                  >
                    <Navigation className="h-3 w-3 mr-2" /> Cómo llegar
                  </a>
                ) : (
                  <Button variant="outline" disabled className="border-zinc-800 bg-transparent text-zinc-600 uppercase tracking-widest text-xs">
                    <Navigation className="h-3 w-3 mr-2" /> Sin dirección
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Map */}
          {mapsEmbed && ticket.status !== "cancelled" && (
            <div className="border-t border-[#262626]" data-testid="ticket-map">
              <iframe
                title="Mapa del club"
                src={mapsEmbed}
                className="w-full h-64 grayscale-[20%]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}

          {ticket.status === "cancelled" ? (
            <div className="p-6 bg-red-500/5 border-t border-red-500/20 flex items-center gap-3 text-red-400" data-testid="ticket-cancelled">
              <XCircle className="h-5 w-5 shrink-0" />
              <div>
                <div className="uppercase tracking-widest text-xs font-bold">Turno cancelado</div>
                <div className="text-xs text-zinc-500 mt-0.5">Si querés reservar otro, volvé a la página del club.</div>
              </div>
            </div>
          ) : ticket.can_cancel ? (
            <div className="p-6 border-t border-[#262626]">
              <div className="flex items-start gap-3 mb-4 text-xs text-zinc-400">
                <Clock className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  Podés cancelar hasta <b className="text-white">{ticket.cancel_cutoff_hours}h</b> antes del turno
                  {cancelDeadline && <> · límite: <b className="text-white">{format(cancelDeadline, "d MMM HH:mm", { locale: es })}</b></>}
                </div>
              </div>
              <Button
                onClick={cancelNow}
                disabled={cancelling}
                variant="outline"
                className="w-full border-red-500/30 text-red-400 bg-transparent hover:bg-red-500/10 uppercase tracking-widest font-bold"
                data-testid="ticket-cancel-btn"
              >
                {cancelling ? "Cancelando..." : "Cancelar este turno"}
              </Button>
            </div>
          ) : (
            <div className="p-6 border-t border-[#262626] flex items-center gap-3 text-zinc-500 text-xs" data-testid="ticket-cancel-closed">
              <Check className="h-4 w-4" />
              Ya no se puede cancelar online. Contactá al club directamente.
            </div>
          )}
        </div>

        <p className="text-center text-zinc-600 text-xs mt-6">
          Guardá este link. Es único para tu turno.
        </p>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value, big, brand }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="h-4 w-4 text-zinc-500 mt-0.5 shrink-0" strokeWidth={1.5} />}
      <div className={Icon ? "" : "pl-7"}>
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-0.5">{label}</div>
        <div className={big ? "font-heading text-2xl" : ""} style={big ? { color: brand } : {}}>{value}</div>
      </div>
    </div>
  );
}
